import { z } from 'zod'
import {
  AMBrand,
  AMGeometryMultiPolygon,
  amGeometryMultiPolygonSchema,
  AMGeometryPolygon,
  amGeometryPolygonSchema,
  amLocationShema,
  amOwnershipEntityPreviewResponseSchema,
  amOwnershipGetParcelByIdResponseSchema,
  amPointSchema,
  Circle,
} from 'src/common-services'
import { GooglePlace } from 'src/utils/google-places'

// All supported ids that can connect to the different data sets we have
export const SUPPORTED_IDS = {
  ALPHAMAP_LOCATION: 'am-loc',
  ALPHAMAP_BRAND: 'am-brand',
  // In the future, we can add more ids here
  ALPHAMAP_PARCEL: 'am-opa',
  ALPHAMAP_OWNER: 'am-oen',
  // ALPHAMAP_ORGANIZATION: 'am-org',
  // PARCEL: 'apn',
  GOOGLE: 'google',
} as const

const idsSchema = z
  .object({
    [SUPPORTED_IDS['ALPHAMAP_LOCATION']]: z.string().optional(),
    [SUPPORTED_IDS['ALPHAMAP_BRAND']]: z.string().optional(),
    [SUPPORTED_IDS['GOOGLE']]: z.string().optional(),
    [SUPPORTED_IDS['ALPHAMAP_PARCEL']]: z.string().optional(),
    [SUPPORTED_IDS['ALPHAMAP_OWNER']]: z.string().optional(),
    // Add more ids here
  })
  .strict()

// For custom areas — this can be a point or polygon or multipolygon
const geometrySchema = z.custom<AMGeometryPolygon | AMGeometryMultiPolygon | Circle>()

// The search entity schema that will be the catch-all for all 'entry point' data
const searchEntitySchema = z.object({
  ids: idsSchema,
  geometry: geometrySchema.optional(),
  address: z.string().optional(), // TODO(ss): should be lib-postal address format
})
export type SearchEntity = z.infer<typeof searchEntitySchema>

// Providers information schema for the expanded entity
const providersSchema = z.object({
  [idsSchema.keyof().enum['google']]: z.custom<GooglePlace>().optional(),
  [idsSchema.keyof().enum['am-loc']]: amLocationShema.optional(),
  [idsSchema.keyof().enum['am-brand']]: z.custom<AMBrand>().optional(),
  [idsSchema.keyof().enum['am-opa']]: amOwnershipGetParcelByIdResponseSchema.optional(),
  [idsSchema.keyof().enum['am-oen']]: amOwnershipEntityPreviewResponseSchema.optional(),
  // Add more providers here
})

// Expanded entity = Base entity + providers information derived from the using base entity data
const expandedEntitySchema = z.object({
  ...searchEntitySchema.shape,
  providers: providersSchema,
})
export type ExpandedEntity = z.infer<typeof expandedEntitySchema>

// Final selection of attributes + info from the expanded entity
const selectedAttributesSchema = z
  .object({
    ids: idsSchema.optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    latlng: amPointSchema.optional(),
    area: z.union([amGeometryPolygonSchema, amGeometryMultiPolygonSchema]).optional(),
    brandId: z.string().optional(),
    photos: z.custom<google.maps.places.PlacePhoto[]>().optional(),
    naicsCode: z.string().optional(),
    // Add more attributes here
    // ...

    // Allow access to raw providers data
    providers: providersSchema,
  })
  .strict()
export type SelectedAttributes = z.infer<typeof selectedAttributesSchema>

const selectedEntityTypeSchema = z.enum([
  'SELECTED_LOCATION',
  'SELECTED_SHOPPING_CENTER',
  'SELECTED_BRAND',
  'SELECTED_ADDRESS',
  'SELECTED_AREA',
  'SELECTED_PARCEL',
  'SELECTED_OWNER',
])
export type SelectedEntityType = z.infer<typeof selectedEntityTypeSchema>

const selectedLocationSchema = z.object({
  type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_LOCATION),
  data: z
    .object({
      ['am-loc']: providersSchema.shape['am-loc'],
      ['google']: providersSchema.shape['google'],
    })
    .required(),
})

const selectedShoppingCenterSchema = z.object({
  type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_SHOPPING_CENTER),
  data: z
    .object({
      ['am-loc']: providersSchema.shape['am-loc'],
      ['google']: providersSchema.shape['google'],
    })
    .required(),
})

const selectedBrandSchema = z
  .object({
    type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_BRAND),
    data: providersSchema.shape['am-brand'],
  })
  .required()
const selectedAddressSchema = z
  .object({
    type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_ADDRESS),
    data: providersSchema.shape['google'],
  })
  .required()

const selectedAreaSchema = z
  .object({
    type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_AREA),
    data: geometrySchema,
  })
  .required()

const selectedParcelSchema = z
  .object({
    type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_PARCEL),
    data: providersSchema.shape['am-opa'],
  })
  .required()

const selectedOwnerSchema = z
  .object({
    type: z.literal(selectedEntityTypeSchema.Enum.SELECTED_OWNER),
    data: providersSchema.shape['am-oen'],
  })
  .required()

// i.e. the selected entity is one of the following
const selectedEntitySchema = z.union([
  selectedLocationSchema,
  selectedShoppingCenterSchema,
  selectedBrandSchema,
  selectedAddressSchema,
  selectedAreaSchema,
  selectedParcelSchema,
  selectedOwnerSchema,
])

export type SelectedEntity = z.infer<typeof selectedEntitySchema>
